import React from 'react';

import { Dinosaur } from '../../types';
import { richTextToString } from '../../utils/richTextUtils';

import CreaturePlaceholder from './CreaturePlaceholder';
import OptimizedFavoriteStarButton from './OptimizedFavoriteStarButton';

export interface CreatureCardProps {
  creature: Dinosaur;
  showFavoriteButton?: boolean;
  className?: string;
}

const CreatureCard: React.FC<CreatureCardProps> = ({
  creature,
  showFavoriteButton = false,
  className = '',
}) => {
  const {
    id,
    documentId,
    name_jp,
    name_en,
    scientificName,
    description_for_kids,
    image,
    classification,
    size,
    diet,
  } = creature;

  return (
    <div
      className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border border-gray-200 cursor-pointer group ${className}`}
    >
      <a href={`/creatures/${documentId || id}`} className="block">
        <div className="relative">
          <div className="aspect-square bg-gray-100 rounded-t-lg flex items-center justify-center group-hover:bg-gray-50 transition-colors">
            {image ? (
              <img
                src={image}
                alt={name_jp}
                className="w-full h-full object-cover rounded-t-lg group-hover:scale-105 transition-transform duration-200"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <CreaturePlaceholder size="sm" />
              </div>
            )}
          </div>

          {/* お気に入りボタン */}
          {showFavoriteButton && (
            <OptimizedFavoriteStarButton
              dinosaurId={documentId || String(id)}
              size="md"
              position="top-right"
            />
          )}

          {/* 分類バッジ */}
          {classification && (
            <div className="absolute bottom-2 left-2 flex flex-wrap gap-1">
              {classification
                .split(/[、。・]|(?<=\))\s*(?=\w)|(?<=\w)\s*(?=\()/)
                .filter(tag => tag.trim() !== '')
                .map((tag, index) => (
                  <span
                    key={index}
                    className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium"
                  >
                    {tag.trim()}
                  </span>
                ))}
            </div>
          )}
        </div>

        <div className="p-4">
          <h3 className="font-semibold text-gray-800 mb-1 group-hover:text-amber-700 transition-colors text-lg">
            {name_jp}
          </h3>

          {name_en && (
            <p className="text-sm text-gray-600 italic mb-2 group-hover:text-amber-600 transition-colors">
              {name_en}
            </p>
          )}

          {scientificName && (
            <p className="text-xs text-gray-500 italic mb-2 group-hover:text-gray-600 transition-colors">
              {scientificName}
            </p>
          )}

          {description_for_kids && (
            <p className="text-xs text-gray-500 line-clamp-2 group-hover:text-gray-600 transition-colors mb-2">
              {richTextToString(description_for_kids)}
            </p>
          )}

          <div className="flex flex-wrap gap-2 text-xs">
            {size && (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">サイズ: {size}</span>
            )}
            {diet && (
              <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded">食性: {diet}</span>
            )}
          </div>
        </div>
      </a>
    </div>
  );
};

export default CreatureCard;
